/**
 * Currency Formatting Utilities
 * 
 * Provides centralized currency formatting functions that use
 * system regional configuration by default.
 * 
 * Requirements: 1.5, 4.1-4.5, 6.1, 6.3, 8.1-8.5
 */

// ==================== Constants ====================

export const DEFAULT_CURRENCY = 'MXN';
export const DEFAULT_LOCALE = 'es-MX';

/**
 * Mapping of country codes to default currencies
 */
const CURRENCY_MAPPINGS: Record<string, string> = {
  MX: 'MXN',
  US: 'USD',
  CA: 'CAD',
  ES: 'EUR',
  AR: 'ARS',
  CO: 'COP',
  CL: 'CLP',
  PE: 'PEN',
  BR: 'BRL',
  FR: 'EUR',
  DE: 'EUR',
  IT: 'EUR',
  GB: 'GBP',
  AU: 'AUD',
  NZ: 'NZD',
  JP: 'JPY',
  CN: 'CNY',
  IN: 'INR',
};

/**
 * Mapping of country codes to locales
 */
const LOCALE_MAPPINGS: Record<string, string> = {
  MX: 'es-MX',
  US: 'en-US',
  CA: 'en-CA',
  ES: 'es-ES',
  AR: 'es-AR',
  CO: 'es-CO',
  CL: 'es-CL',
  PE: 'es-PE',
  BR: 'pt-BR',
  FR: 'fr-FR',
  DE: 'de-DE',
  IT: 'it-IT',
  GB: 'en-GB',
  AU: 'en-AU',
  NZ: 'en-NZ',
  JP: 'ja-JP',
  CN: 'zh-CN',
  IN: 'hi-IN',
};

// ==================== Cache ====================

/**
 * Cache for Intl.NumberFormat instances to improve performance
 * Key format: "locale-currency-style"
 */
const numberFormatCache = new Map<string, Intl.NumberFormat>();

/**
 * Get or create a cached NumberFormat instance
 */
function getNumberFormatter(
  locale: string,
  currency: string,
  style: 'currency' | 'decimal' = 'currency',
  fractionDigits: number = 2
): Intl.NumberFormat {
  const key = `${locale}-${currency}-${style}-${fractionDigits}`;
  
  if (!numberFormatCache.has(key)) {
    const options: Intl.NumberFormatOptions = {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    };
    
    if (style === 'currency') {
      options.style = 'currency';
      options.currency = currency;
    }
    
    numberFormatCache.set(key, new Intl.NumberFormat(locale, options));
  }
  
  return numberFormatCache.get(key)!;
}

// ==================== Public Functions ====================

/**
 * Get default currency for a country code
 * 
 * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., 'MX', 'US')
 * @returns Currency code (e.g., 'MXN', 'USD')
 * 
 * @example
 * getCurrencyFromCountry('MX') // 'MXN'
 * getCurrencyFromCountry('US') // 'USD'
 * getCurrencyFromCountry('XX') // 'MXN' (fallback)
 * 
 * Requirements: 4.1-4.4, 8.1-8.3
 */
export function getCurrencyFromCountry(countryCode?: string): string {
  if (!countryCode) return DEFAULT_CURRENCY;
  return CURRENCY_MAPPINGS[countryCode.toUpperCase()] || DEFAULT_CURRENCY;
}

/**
 * Get locale for a country code
 * 
 * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., 'MX', 'US')
 * @returns Locale string (e.g., 'es-MX', 'en-US')
 * 
 * @example
 * getLocaleFromCountry('MX') // 'es-MX'
 * getLocaleFromCountry('US') // 'en-US'
 * getLocaleFromCountry('XX') // 'es-MX' (fallback)
 * 
 * Requirements: 4.1-4.4
 */
export function getLocaleFromCountry(countryCode?: string): string {
  if (!countryCode) return DEFAULT_LOCALE;
  return LOCALE_MAPPINGS[countryCode.toUpperCase()] || DEFAULT_LOCALE;
}

/**
 * Format a number as currency
 * 
 * @param amount - The amount to format
 * @param currency - Currency code (default: MXN)
 * @param locale - Locale for formatting (default: es-MX)
 * @param options - Formatting options
 * @param options.showCode - Show currency code instead of symbol (e.g., "1.299 MXN" instead of "$1,299")
 * @param options.fractionDigits - Number of decimal places (default is 2)
 * @returns Formatted currency string
 * 
 * @example
 * formatPrice(1299.99, 'MXN', 'es-MX') // '$1,299.99'
 * formatPrice(1299.99, 'MXN', 'es-MX', { showCode: true }) // '1.299 MXN'
 * formatPrice(1299.99, 'MXN', 'es-MX', { fractionDigits: 0 }) // '$1,300'
 * formatPrice(1299.99, 'USD', 'en-US') // '$1,299.99'
 * formatPrice(1299.99, 'EUR', 'es-ES') // '1.299,99 €'
 * 
 * Requirements: 6.1, 6.3, 8.1-8.5
 */
export function formatPrice(
  amount: number,
  currency: string = DEFAULT_CURRENCY,
  locale: string = DEFAULT_LOCALE,
  options?: { showCode?: boolean; fractionDigits?: number }
): string {
  try {
    const digits = options?.fractionDigits !== undefined ? options.fractionDigits : 2;

    if (options?.showCode) {
      // Format with currency code instead of symbol
      const formatter = new Intl.NumberFormat(locale, {
        style: 'decimal',
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });
      const formattedNumber = formatter.format(amount);
      return `${formattedNumber} ${currency}`;
    }
    
    const formatter = getNumberFormatter(locale, currency, 'currency', digits);
    return formatter.format(amount);
  } catch (error) {
    console.error('Error formatting price:', error);
    // Fallback to simple formatting
    const digits = options?.fractionDigits !== undefined ? options.fractionDigits : 2;
    if (options?.showCode) {
      return `${amount.toFixed(digits)} ${currency}`;
    }
    return `${currency} ${amount.toFixed(digits)}`;
  }
}

/**
 * Format a number without currency symbol
 * 
 * @param amount - The amount to format
 * @param locale - Locale for formatting (default: es-MX)
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted number string
 * 
 * @example
 * formatNumber(1299.99, 'es-MX') // '1,299.99'
 * formatNumber(1299.99, 'de-DE') // '1.299,99'
 * formatNumber(1299.999, 'en-US', 3) // '1,299.999'
 * 
 * Requirements: 6.3, 8.4
 */
export function formatNumber(
  amount: number,
  locale: string = DEFAULT_LOCALE,
  decimals: number = 2
): string {
  try {
    const formatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    return formatter.format(amount);
  } catch (error) {
    console.error('Error formatting number:', error);
    // Fallback to simple formatting
    return amount.toFixed(decimals);
  }
}

/**
 * Clear the number format cache
 * Useful for testing or when memory optimization is needed
 */
export function clearNumberFormatCache(): void {
  numberFormatCache.clear();
}
